import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { all, call, delay, put, select, takeLatest } from 'redux-saga/effects';

import { getItemCount } from 'cart/common/helpers';
import { doesCustomerExist } from 'common/helpers/customerHelper';
import { hasForterTokenCookie } from 'common/helpers/featureFlags';
import { currencyFormat } from 'common/helpers/numberHelper';
import { customerId } from 'common/helpers/tokenHelper';
import { RECORD_EVENT_REQUEST } from 'redux/analytics/constants';
import { addAPIError, removeAPIError } from 'redux/apiErrors/sagas';
import { getCustomer } from 'redux/customer/api';
import { addCustomerAddress } from 'redux/customerAddresses/api';

import {
  addCouponCode,
  addGiftCard,
  addItem,
  addLoyaltyTender,
  authenticateForFullSteamWallet,
  chargeCart,
  createCart,
  createPaymentIntentCart,
  chargeFullSteamWallet,
  getCart,
  getCartForSharing,
  removeItem,
  removeTender,
  replaceTenderOnCart,
  sendCartToHostedPayment,
  updateCart,
  updateItem,
  verifyPayStackAPI
} from './api';
import {
  ADD_CART_ITEM_REQUEST,
  ADD_COUPON_REQUEST,
  ADD_GIFT_CARD_REQUEST,
  ADD_LOYALTY_TENDER_REQUEST,
  AUTH_FULLSTEAM_WALLET_REQUESTING,
  AUTH_FULLSTEAM_WALLET_SUCCESS,
  CART_IS_ITEM_BEING_ADDED,
  CHARGE_CART_REQUEST,
  CREATE_CART_PAYMENT_INTENT_REQUEST,
  CHARGE_FULLSTEAM_WALLET_REQUESTING,
  CHARGE_FULLSTEAM_WALLET_SUCCESS,
  CHARGE_FULLSTEAM_WALLET_REQUEST,
  GET_CART_FOR_SHARING_REQUEST,
  GET_CART_FROM_PAY_GATE_REQUEST,
  GET_CART_OBJECT_REQUESTING,
  GET_CART_REQUEST,
  GET_CART_SUCCESS,
  GET_AUTH_FULLSTEAM_WALLET,
  REMOVE_CART_ITEM_REQUEST,
  REMOVE_TENDER_REQUEST,
  REPLACE_TENDER_ON_CART_REQUEST,
  RESET_CART_ISFETCHING,
  RESET_FULLSTEAM_WALLET_ISFETCHING,
  SEND_CART_TO_PAY_GATE_REQUEST,
  SIDE_CART_VISIBLE,
  UPDATE_CART_ITEM_REQUEST,
  UPDATE_CART_REQUEST,
  VERIFY_PAYSTACK_REQUEST
} from './constants';

export function* get() {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    let hasCart = false;
    if (customerId()) {
      const response = yield call(getCustomer);

      const { cart } = response.data.orderInformation;
      if (cart) {
        hasCart = true;
        if (cart.status === 'Complete') {
          yield call(emptyCart);
        } else {
          Cookies.set('cartId', cart.id, {
            expires: 365,
            secure: process.env.APP_MODE === 'production'
          });
          yield put({ type: GET_CART_SUCCESS, cart });
        }
      }
    }
    if (!hasCart && cartId()) {
      const response = yield call(getCart, cartId());
      hasCart = true;
      if (response.data.status === 'Complete') {
        yield call(emptyCart);
      } else {
        yield put({ type: GET_CART_SUCCESS, cart: response.data });
      }
    }
    if (!hasCart) {
      yield put({ type: GET_CART_SUCCESS, cart: {} });
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      yield call(emptyCart);
    } else {
      yield put({ type: RESET_CART_ISFETCHING });
      yield call(handleError, error, 'get');
    }
  }
}

function* getForSharing(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const response = yield call(getCartForSharing, action.id, customerId());
    const cart = response.data;
    Cookies.set('cartId', cart.id, {
      expires: 365,
      secure: process.env.APP_MODE === 'production'
    });
    yield put({ type: GET_CART_SUCCESS, cart });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    if (error.response && error.response.status === 404) {
      Cookies.remove('cartId');
      yield put({ type: GET_CART_SUCCESS, cart: {} });
    } else {
      yield put({ type: RESET_CART_ISFETCHING });
      yield call(handleError, error, 'get');
    }
  }
}

function* emptyCart() {
  Cookies.remove('cartId');
  yield put({ type: GET_CART_SUCCESS, cart: {} });
}

function* update(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    if (action.cart.shipTo && customerId()) {
      // create the address if it doesn't exist and the customer does exist
      const { ...newShipto } = action.cart.shipTo;
      if (!newShipto.customerAddressId) {
        delete newShipto.smsMarketing; // deletes the smsMarketing object as it's not needed for creating the address and causes an error
        delete newShipto.customerAddressId; // deletes the null value
        const response = yield call(addCustomerAddress, newShipto);
        action.cart.shipTo.customerAddressId = response.data.id;
      }
    }
    const response = yield call(updateCart, cartId(), action.cart);
    yield put({ type: GET_CART_SUCCESS, cart: response.data });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    yield put({ type: RESET_CART_ISFETCHING });
    if (action.onError) {
      action.onError();
    }
    yield call(handleError, error, 'update');
  }
}

function* authenticateFullSteamWallet(action) {
  try {
    yield call(removeAPIError);

    yield put({ type: AUTH_FULLSTEAM_WALLET_REQUESTING });
    const response = yield call(authenticateForFullSteamWallet, cartId());

    yield put({ type: AUTH_FULLSTEAM_WALLET_SUCCESS, payload: response.data });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    delete error.response.data.errors;
    yield call(addAPIError, error);
    if (action.onError) {
      action.onError(error);
    }
  }
}

function* chargeWallet(action) {
  try {
    yield call(removeAPIError);

    yield put({ type: CHARGE_FULLSTEAM_WALLET_REQUESTING });
    const response = yield call(
      chargeFullSteamWallet,
      cartId(),
      action.hostedPaymentsAccountId
    );
    yield put({
      type: CHARGE_FULLSTEAM_WALLET_SUCCESS,
      payload: response.data
    });
    yield put({
      type: RECORD_EVENT_REQUEST,
      eventName: 'checkoutComplete',
      payload: response.data
    });

    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    delete error.response.data.errors;

    if (action.onError) {
      action.onError(error);
    }
    yield put({ type: RESET_FULLSTEAM_WALLET_ISFETCHING });
    yield call(handleError, error, 'chargeWallet');
  }
}

function* createPaymentIntent(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const response = yield call(
      createPaymentIntentCart,
      cartId(),
      action.params
    );
    yield put({ type: GET_CART_SUCCESS, cart: response.data });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    if (action.onError) {
      action.onError();
    }
    yield call(handleError, error, 'create payment intent');
  }
}

function* replaceTender(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const response = yield call(replaceTenderOnCart, cartId(), action.tender);
    // TODO we should really check to see if cart is completed here
    yield put({ type: GET_CART_SUCCESS, cart: response.data });
    if (action.onSuccess) {
      action.onSuccess(response.data);
    }
  } catch (error) {
    if (action.onError) {
      action.onError();
    }
    yield call(handleError, error, 'replace tender');
  }
}

function* completeCart(cart) {
  // TODO we should really check to see if cart is completed here
  yield put({ type: GET_CART_SUCCESS, cart });
  Cookies.remove('cartId');
}

function* charge(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const cart = yield call(chargeCart, cartId(), {
      recaptchaToken: action.token
    });
    yield call(completeCart, cart.data);
    yield put({
      type: RECORD_EVENT_REQUEST,
      eventName: 'checkoutComplete',
      payload: cart.data
    });
    if (action.onSuccess) {
      action.onSuccess(cart.data);
    }
  } catch (error) {
    if (action.onError) {
      action.onError();
    }
    yield call(handleError, error, 'charge');
  }
}

function* verifyPaystack(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const cart = yield call(verifyPayStackAPI, cartId(), {
      tenderId: action.tenderId
    });
    // TODO we should really check to see if cart is completed here
    yield call(completeCart, cart.data);
    yield put({
      type: RECORD_EVENT_REQUEST,
      eventName: 'checkoutComplete',
      payload: cart.data
    });
    if (action.onSuccess) {
      action.onSuccess(cart.data);
    }
  } catch (error) {
    if (action.onError) {
      action.onError();
    }
    yield call(handleError, error, 'charge');
  }
}

let isCreating = false;

function* create() {
  while (isCreating) {
    yield delay(500);
    yield call(create);
  }
  isCreating = true;
  if (!cartId()) {
    const newCart = {
      channel: 'Web',
      connectionInformation: {
        userAgent: navigator.userAgent,
        ...(hasForterTokenCookie() && {
          forterTokenCookie: Cookies.get('forterToken')
        })
      }
    };
    if (doesCustomerExist()) {
      newCart.customerId = jwtDecode(Cookies.get('customerToken')).sub;
    }
    if (Cookies.get('c7EmailId')) {
      newCart.additionalInfo = {
        emailId: Cookies.get('c7EmailId')
      };
    }
    if (Cookies.get('c7metaData')) {
      newCart.metaData = JSON.parse(Cookies.get('c7metaData'));
    }
    try {
      const cart = yield call(createCart, newCart);
      Cookies.set('cartId', cart.data.id, {
        expires: 365,
        secure: process.env.APP_MODE === 'production'
      });
    } catch {
      delete newCart.metaData;
      const cart = yield call(createCart, newCart);
      Cookies.set('cartId', cart.data.id, {
        expires: 365,
        secure: process.env.APP_MODE === 'production'
      });
    }
  }
  isCreating = false;
}

function* itemAdd(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    yield put({ type: SIDE_CART_VISIBLE });
    yield put({
      type: CART_IS_ITEM_BEING_ADDED,
      skuBeingAdded: action.item.sku
    });
    yield call(create); // Esure we have a cart
    const response = yield call(addItem, cartId(), action.item);
    const settings = yield select(getSettings);
    const message = `Item added to cart.  ${cartMessage(
      response.data,
      settings
    )}`;
    yield put({
      type: GET_CART_SUCCESS,
      cart: response.data,
      cartUpdateMessage: message
    });
    const cartItem = response.data.items.find((i) => i.sku === action.item.sku);
    yield put({
      type: RECORD_EVENT_REQUEST,
      eventName: 'addToCart',
      payload: cartItem
    });
    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'itemAdd');
    if (action.onError) {
      action.onError();
    }
  }
}

const getSettings = (state) => state.setting.data;
const getOriginalCart = (state) => state.cart.data;

const cartMessage = (cart, settings) => {
  const count = getItemCount(cart);
  const message = `You now have ${count} item${
    count !== 1 ? 's' : ''
  } in your cart, ${currencyFormat(cart.total, settings.currency)}`;
  return message;
};

function* itemRemove(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const originalCart = yield select(getOriginalCart);
    const response = yield call(
      removeItem,
      cartId(),
      action.itemId,
      action.useLoyaltyPoints
    );
    const settings = yield select(getSettings);
    const message = `Item removed from cart.  ${cartMessage(
      response.data,
      settings
    )}`;
    yield put({
      type: GET_CART_SUCCESS,
      cart: response.data,
      cartUpdateMessage: message
    });

    const cartItem = originalCart.items.find((i) => i.id === action.itemId);
    yield put({
      type: RECORD_EVENT_REQUEST,
      eventName: 'removeFromCart',
      payload: cartItem
    });
    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'itemRemove');
    if (action.onError) {
      action.onError();
    }
  }
}

function* itemUpdate(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const item = {
      quantity: action.quantity
    };
    if (action.useLoyaltyPoints) {
      item.itemData = {
        useLoyaltyPoints: action.useLoyaltyPoints
      };
    }
    const response = yield call(updateItem, cartId(), action.itemId, item);
    const settings = yield select(getSettings);
    const message = `Item quantity updated.  ${cartMessage(
      response.data,
      settings
    )}`;
    yield put({
      type: GET_CART_SUCCESS,
      cart: response.data,
      cartUpdateMessage: message
    });
    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    if (action.onError) {
      action.onError();
    }
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'itemUpdate');
  }
}

function* couponAdd(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    yield call(create); // Esure we have a cart as we can start with just a coupon
    const response = yield call(addCouponCode, cartId(), {
      code: action.couponCode
    });
    const cart = response.data;
    yield put({ type: GET_CART_SUCCESS, cart });
    const coupon = cart.coupons.find(
      (c) => c.code.toLowerCase() === action.couponCode.toLowerCase()
    );

    if (coupon?.inUse) {
      yield put({
        type: RECORD_EVENT_REQUEST,
        eventName: 'addCoupon',
        payload: coupon
      });
    }

    if (coupon?.inUse === false) {
      action.onError({ data: { message: coupon.invalidReason } });
    }
    if (action.onSuccess) {
      action.onSuccess(coupon?.inUse);
    }
  } catch (error) {
    yield put({ type: RESET_CART_ISFETCHING });
    if (
      error.response &&
      (error.response.status === 404 || error.response.status === 422)
    ) {
      action.onError(error.response);
    } else {
      yield call(handleError, error, 'couponAdd');
    }
  }
}

function* loyaltyTenderAdd(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    yield call(create); // Ensure we have a cart
    const response = yield call(addLoyaltyTender, cartId(), action.tender);
    yield put({ type: GET_CART_SUCCESS, cart: response.data });
    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'loyaltyTenderAdd');
    if (action.onError) {
      action.onError();
    }
  }
}

function* tenderRemove(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const response = yield call(removeTender, cartId(), action.tenderId);
    yield put({ type: GET_CART_SUCCESS, cart: response.data });
    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'removeTender');
    if (action.onError) {
      action.onError();
    }
  }
}

function* giftCardAdd(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    yield call(create); // Esure we have a cart as we can start with just a gift card
    const code = action.giftCardCode.replace(/[^0-9a-z]/gi, '');

    const response = yield call(addGiftCard, cartId(), {
      code
    });
    yield put({ type: GET_CART_SUCCESS, cart: response.data });

    if (action.onSuccess) {
      action.onSuccess();
    }
  } catch (error) {
    if (action.onError) {
      action.onError();
    }
    yield put({ type: RESET_CART_ISFETCHING });
    yield call(handleError, error, 'giftCardAdd');
  }
}

function* handleError(error, callerFunction) {
  // If error is Cart is complete we clear the cart and forward to receipt
  const { response } = error;

  if (
    response?.status === 422 &&
    response?.data?.message === 'Cart is completed and cant be processed'
  ) {
    // If we come from the right caller function
    if (
      ['update', 'updateAndSaveShipping', 'updateAndCharge', 'charge'].includes(
        callerFunction
      )
    ) {
      // Get the cart
      const cart = yield call(getCart, cartId());
      // Clear Cookie
      Cookies.remove('cartId');
      // Forward to receipt page
      if (customerId()) {
        window.location.href = `/checkout/receipt/${cart.data.orderId}`;
      } else {
        window.location.href = `/checkout/receipt?orderNumber=${cart.data.orderNumber}&email=${cart.data.email}`;
      }
    } else {
      // Clear Cookie
      Cookies.remove('cartId');
    }
  } else if (response?.data?.key === 'chargeError') {
    error.response.data.message =
      'Payment could not be completed. Please check or update your payment information and try again';
    yield call(addAPIError, error);
  } else {
    yield call(addAPIError, error);
  }
}

function* sendToPayGate(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const response = yield call(sendCartToHostedPayment, cartId());
    action.redirectFunction(response.data);
  } catch (error) {
    yield call(handleError, error, 'charge');
  }
}

export function* getForPayGate(action) {
  try {
    yield call(removeAPIError);
    yield put({ type: GET_CART_OBJECT_REQUESTING });
    const response = yield call(getCart, cartId());
    yield put({ type: GET_CART_SUCCESS, payload: response.data });
    const cart = response.data;
    if (cart.status === 'Completed') {
      yield put({ type: GET_CART_SUCCESS, payload: response.data });
      Cookies.remove('cartId');
      action.onSuccess();
    } else {
      const isWaitingTender = cart.tenders.find(
        (tender) => tender.chargeStatus === 'Waiting'
      );
      if (!isWaitingTender) {
        const err = {
          response: {
            status: 402,
            data: {
              message: 'Payment Declined'
            }
          }
        };
        yield call(handleError, err, 'updateAndCharge');
      }
    }
  } catch (error) {
    yield call(handleError, error, 'updateAndCharge');
  }
}

function* watchList() {
  yield takeLatest(GET_CART_REQUEST, get);
  yield takeLatest(GET_CART_FOR_SHARING_REQUEST, getForSharing);
  yield takeLatest(GET_AUTH_FULLSTEAM_WALLET, authenticateFullSteamWallet);
  yield takeLatest(UPDATE_CART_REQUEST, update);
  yield takeLatest(REPLACE_TENDER_ON_CART_REQUEST, replaceTender);
  yield takeLatest(CHARGE_CART_REQUEST, charge);
  yield takeLatest(CHARGE_FULLSTEAM_WALLET_REQUEST, chargeWallet);
  yield takeLatest(VERIFY_PAYSTACK_REQUEST, verifyPaystack);
  yield takeLatest(ADD_CART_ITEM_REQUEST, itemAdd);
  yield takeLatest(REMOVE_CART_ITEM_REQUEST, itemRemove);
  yield takeLatest(UPDATE_CART_ITEM_REQUEST, itemUpdate);
  yield takeLatest(CREATE_CART_PAYMENT_INTENT_REQUEST, createPaymentIntent);
  yield takeLatest(ADD_COUPON_REQUEST, couponAdd);
  yield takeLatest(ADD_GIFT_CARD_REQUEST, giftCardAdd);
  yield takeLatest(ADD_LOYALTY_TENDER_REQUEST, loyaltyTenderAdd);
  yield takeLatest(REMOVE_TENDER_REQUEST, tenderRemove);
  yield takeLatest(SEND_CART_TO_PAY_GATE_REQUEST, sendToPayGate);
  yield takeLatest(GET_CART_FROM_PAY_GATE_REQUEST, getForPayGate);
}

export default function* cartSagas() {
  yield all([watchList()]);
}

const cartId = () => Cookies.get('cartId');
